/*
 * https://github.com/mikecousins/react-pdf-js
 * 한 화면에서 여러 페이지를 동시에 렌더링할 수 있도록 변경
 */

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PdfJsLib from 'pdfjs-dist';
import {
  filter, forEach, isUndefined, range,
} from 'lodash/fp';

const PDF_WORKER_URL = '//cdnjs.cloudflare.com/ajax/libs/pdf.js';
const VERSION = '2.1.266';

const usePdf = ({
  ref,
  file,
  scale = 1,
  rotate = 0,
  pageNumber = 1,
  cMapUrl,
  cMapPacked,
  onDrawComplete = () => {},
  workerSrc = `${PDF_WORKER_URL}/${VERSION}/pdf.worker.js`,
  withCredentials = false,
  onError,
}) => {
  const [pdf, setPdf] = useState(null);
  const [canvasRefs, setCanvasRefs] = useState(null);
  const canvases = useRef(null);
  const loading = useMemo(() => !pdf, [pdf]);
  /* eslint-disable no-underscore-dangle */
  const numPages = useMemo(() => (pdf ? pdf._pdfInfo.numPages : null), [pdf]);
  /* eslint-enable */
  const drawPDF = useCallback((page, index) => {
    const rotation = rotate === 0 ? page.rotate : page.rotate + rotate;
    const dpRatio = window.devicePixelRatio;
    const adjustedScale = scale * dpRatio;
    const viewport = page.getViewport({ scale: adjustedScale, rotation });
    const element = ref.current;

    if (!element) {
      return;
    }

    const canvas = element instanceof HTMLCanvasElement
      ? element : filter(el => el instanceof HTMLCanvasElement, element.children)[index];
    const canvasContext = canvas.getContext('2d');

    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = { canvasContext, viewport };

    page.render(renderContext);

    const isCanvasRef = isUndefined(index);

    if (!isCanvasRef) {
      canvases.current = canvases.current
        ? [...canvases.current, canvas]
        : [canvas];
    }

    if (isCanvasRef || numPages === index + 1) {
      setCanvasRefs(isCanvasRef ? canvas : canvases.current);
    }
  }, [numPages, ref, rotate, scale]);

  useEffect(() => {
    canvases.current = null;
  }, [file]);

  useEffect(() => {
    PdfJsLib.GlobalWorkerOptions.workerSrc = workerSrc;
  }, [workerSrc]);

  useEffect(() => {
    let isSubscribed = true;

    const config = { url: file, withCredentials };

    if (cMapUrl) {
      config.cMapUrl = cMapUrl;
      config.cMapPacked = cMapPacked;
    }

    PdfJsLib.getDocument(config).promise
      .then((loadedPdf) => {
        if (isSubscribed) {
          setPdf(loadedPdf);
        }
      }).catch((e) => {
        setPdf(null);

        if (onError) {
          onError();
        } else {
          throw e;
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [cMapPacked, cMapUrl, file, onError, withCredentials]);

  useEffect(() => {
    if (pdf) {
      const element = ref.current;
      if (element instanceof HTMLCanvasElement) {
        pdf.getPage(pageNumber).then(page => drawPDF(page));
      } else {
        forEach.convert({ cap: false })((num, index) => {
          pdf.getPage(num).then(page => drawPDF(page, index));
        }, range(1, numPages + 1));
      }
    }
  }, [drawPDF, numPages, pageNumber, pdf, ref]);

  useEffect(() => {
    if (canvasRefs) {
      onDrawComplete(canvasRefs);
    }
  }, [canvasRefs, onDrawComplete]);

  return [loading, numPages];
};

export default usePdf;
